import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { DEFAULT_CONFIG } from '@qapline/config';
import { Spinner } from '@qapline/ui';

import { useAppGuard, useAuth } from '../../hooks';
import { useRefreshToken } from '../../hooks/useRefreshToken';
import { EAuthRole } from '../../types';

export interface IAuthenticatedProps {
    children: React.ReactNode;
    /**
     * Roles permitted to access this app. A user authenticated with any other
     * role is logged out and redirected to login. Omit to allow any role.
     */
    allowedRoles?: EAuthRole[];
}

export const Authenticated = ({ children, allowedRoles }: IAuthenticatedProps) => {
    const { isAuthenticated } = useAuth();

    const { isLoading: isRefreshingToken, error } = useRefreshToken();
    const isRoleAllowed = useAppGuard(allowedRoles);

    useEffect(() => {
        if (error) {
            console.error('Error during token refresh:', error);
        }
    }, [error]);

    if (isRefreshingToken || isAuthenticated === null) {
        return <Spinner fullScreen />;
    }

    if (isAuthenticated === false) {
        return <Navigate to={DEFAULT_CONFIG.routes.auth.login} replace={true} />;
    }

    // Role not permitted in this app: useAppGuard is logging the user out, which
    // flips isAuthenticated to false and triggers the redirect above on re-render.
    if (!isRoleAllowed) {
        return <Spinner fullScreen />;
    }

    return <>{children}</>;
};
